import { api } from '@/lib/api/requests'
import { plusTaxMinor as plusTax, readTaxFields } from '@/features/payments/taxDisplay'
import { withTaxedShare } from './shareTax'
import type { BookingRow, CancelBookingResponse, MyBookingsResponse } from './types'

/**
 * A WHOLE BOOKING'S MONEY, TAX INCLUDED (D3.16, ADR §7.7). A repriced row names its twins; any other
 * taxed row states one figure (`amountDue`, else money `amountPaid`) with its server total.
 */
function foldWholeTax(row: BookingRow): BookingRow {
  if (row.amountPaidTaxMinor != null || row.amountDueTaxMinor != null) {
    const change = row.priceChange
    return {
      ...row,
      amountPaid: plusTax(row.amountPaid, row.amountPaidTaxMinor),
      amountDue: plusTax(row.amountDue, row.amountDueTaxMinor),
      ...(change
        ? {
            priceChange: {
              ...change,
              settledPrice: plusTax(change.settledPrice, change.settledTaxMinor) ?? change.settledPrice,
              creditedBack: plusTax(change.creditedBack, change.creditedBackTaxMinor) ?? change.creditedBack,
            },
          }
        : {}),
    }
  }
  const line = readTaxFields(row)
  if (!line) return row
  if (row.amountDue != null) return { ...row, amountDue: line.totalMinor / 100 }
  return Number(row.amountPaid) > 0 ? { ...row, amountPaid: line.totalMinor / 100 } : row
}

/** D3.16: every share a row carries owes its server total when taxed — folded in once, here. */
function foldShareTax(source: BookingRow): BookingRow {
  const row = foldWholeTax(source)
  const yourShare = row.split?.yourShare
  return {
    ...row,
    ...(row.split && yourShare ? { split: { ...row.split, yourShare: withTaxedShare(yourShare) } } : {}),
    ...(row.dependentShares ? { dependentShares: row.dependentShares.map(withTaxedShare) } : {}),
    ...(row.payingForShares ? { payingForShares: row.payingForShares.map(withTaxedShare) } : {}),
  }
}

export async function fetchMyBookings(): Promise<MyBookingsResponse> {
  const body = await api.get<MyBookingsResponse>('/me/bookings', { handleAuthErrors: false })
  if (!body || !Array.isArray(body.upcoming) || !Array.isArray(body.past)) return body
  return { ...body, upcoming: body.upcoming.map(foldShareTax), past: body.past.map(foldShareTax) }
}

export async function cancelBooking(bookingId: string): Promise<CancelBookingResponse> {
  return api.post<CancelBookingResponse>(
    `/me/bookings/${encodeURIComponent(bookingId)}/cancel`,
    { handleAuthErrors: false },
  )
}
